import { VoiceCardController } from "../utils/voice_card_controller"

// Voice input (BETA) for ONE card of the custom report form
// (CustomReports::VoiceCardComponent). The card — recording, transcribe and
// extract steps, states, tags, hints, undo — is VoiceCardController; this
// controller knows the report form:
//
// - The extract step sends the card's field keys. Once the card holds values
//   (voice-applied, typed or defaults) they go along as `draft` and only the
//   server's changed_keys are applied, so a model that mangles an unmentioned
//   field cannot clobber what is already there.
// - Values are written through the field partials' inputs with input/change
//   events, so `dynamic-form` re-evaluates conditions; SlimSelect through the
//   select controller, dates through the hidden half of Common::DateFieldComponent.
// - Fixed-row tables (CustomReports::VoiceTable) take cells: the request names them
//   in table_ids, the draft carries their filled cells, and a suggested cell is
//   written into the row of that label — rows are never added.
// - "Rückgängig" puts back this card's fields and cells only.
// - Only one card records at a time: starting announces itself on window and
//   every other card disables its microphone until it is done.
const BUSY_EVENT = "custom-report-voice:busy"
const FREE_EVENT = "custom-report-voice:free"
const ACTIVE_STATES = ["arming", "recording", "processing", "finalizing"]
const FIELD_NAME = /^custom_report\[field_data\]\[([^\]]+)\]/
const LONG_TEXT_TYPES = ["text"]
const BLOCK_ID_SUFFIX = "[custom_report_block_id]"

export default class extends VoiceCardController {
  static values = {
    // key => { label, type } of the card's answerable fields
    fields: Object,
    // block id => { label, rows, columns: { key => { label, type } } } of the card's fixed-row tables
    tables: { type: Object, default: {} },
    cardLabel: String,
    reportId: String,
    reportableType: String,
    reportableId: String,
    freeTextPrefix: { type: String, default: "text:" },
  }

  connect() {
    super.connect()
    this.onBusy = (event) => {
      if (event.detail?.element !== this.element) this.setBlocked(true)
    }
    this.onFree = () => this.setBlocked(false)
    window.addEventListener(BUSY_EVENT, this.onBusy)
    window.addEventListener(FREE_EVENT, this.onFree)
  }

  disconnect() {
    window.removeEventListener(BUSY_EVENT, this.onBusy)
    window.removeEventListener(FREE_EVENT, this.onFree)
    if (this.announced) this.announce(FREE_EVENT)
    super.disconnect()
  }

  start() {
    if (this.blocked) return
    super.start()
  }

  setState(state) {
    super.setState(state)
    const active = ACTIVE_STATES.includes(state)
    if (active && !this.announced) this.announce(BUSY_EVENT)
    if (!active && this.announced) this.announce(FREE_EVENT)
    this.announced = active
    if (this.hasMicTarget && this.blocked) this.micTarget.disabled = true
  }

  // --- VoiceCardController hooks ---

  extractRequest(token) {
    const body = {
      transcript_token: token,
      field_keys: this.keys(),
      card_label: this.cardLabelValue,
      ...this.reportParams(),
    }
    // The form's report date is the reference for "heute"/"gestern".
    const date = this.form().querySelector('[name="custom_report[report_date]"]')?.value
    if (date) body.date = date
    const tableIds = Object.keys(this.tablesValue)
    if (tableIds.length) body.table_ids = tableIds
    const draft = this.readCard()
    const cells = this.readCells().filter((cell) => !blank(cell.value))
    const correction = Object.values(draft).some((value) => !blank(value)) || cells.length > 0
    if (correction) body.draft = JSON.stringify({ field_data: draft, table_cells: cells })
    return { body, correction }
  }

  transcribeParams() {
    return this.reportParams()
  }

  takeSnapshot() {
    return { fields: this.readCard(), cells: this.readCells() }
  }

  restoreSnapshot(snapshot) {
    Object.entries(snapshot.fields).forEach(([key, value]) => this.write(key, value))
    snapshot.cells.forEach((cell) => this.writeCell(cell))
  }

  applyPayload(payload) {
    const changedKeys = payload.changed_keys ? payload.changed_keys.field_data || [] : null
    const values = payload.suggestions?.field_data || {}
    const missing = []
    let applied = 0
    Object.entries(values).forEach(([key, value]) => {
      if (changedKeys && !changedKeys.includes(key)) return
      if (this.write(key, value)) {
        applied += 1
      } else {
        missing.push(key)
      }
    })
    this.cellTitles = new Map()
    const changedCells = payload.changed_keys ? payload.changed_keys.table_cells || [] : null
    ;(payload.suggestions?.table_cells || []).forEach((cell) => {
      if (changedCells && !changedCells.includes(cellKey(cell))) return
      if (this.writeCell(cell)) {
        applied += 1
      } else {
        missing.push(this.cellTitle(cell))
      }
    })
    // Conditions re-evaluate on the change events written above (dynamic-form).
    this.hiddenKeys = Object.keys(values).filter(
      (key) => (!changedKeys || changedKeys.includes(key)) && this.hiddenByCondition(key),
    )
    return { applied, missing }
  }

  hints(warnings, missing) {
    const hidden = (this.hiddenKeys || []).map((key) =>
      this.message("hiddenField").replace("{field}", this.label(key)),
    )
    return [...super.hints(warnings, missing), ...hidden]
  }

  // The fields the pass changed as tiles.
  renderSummary(changed) {
    const chips = changed
      .map((name) => name.match(FIELD_NAME)?.[1])
      .filter((key, index, keys) => key && key in this.fieldsValue && keys.indexOf(key) === index)
      .map((key) => this.summaryChip(key))
      .concat(changed.map((name) => this.cellChip(name)))
      .filter(Boolean)
    if (!chips.length) {
      this.summaryTarget.replaceChildren()
      return
    }
    const grid = document.createElement("div")
    grid.className = "grid grid-cols-2 gap-2 sm:grid-cols-3"
    grid.append(...chips)
    this.summaryTarget.replaceChildren(grid)
  }

  // A tile per field; free text gets two columns and up to two lines instead of
  // being cut off.
  summaryChip(key) {
    const chip = this.chip(this.label(key), this.display(key))
    if (chip && LONG_TEXT_TYPES.includes(this.fieldsValue[key]?.type)) {
      chip.classList.add("col-span-2")
    }
    return chip
  }

  // A tile per written table cell: "Arbeitskräfte · Polier · Anzahl".
  cellChip(name) {
    const cell = this.cellTitles?.get(name)
    if (!cell) return null
    const input = this.form().querySelector(`[name="${name}"]:not([type="hidden"])`)
    if (!input) return null
    if (cell.type === "checkbox")
      return this.chip(cell.title, input.checked ? this.message("yes") : "")
    const value = cell.type === "number" ? input.value.replace(".", ",") : input.value
    return this.chip(cell.title, value)
  }

  // --- Table cells ---

  // { block_id, row_label, column_key, value } of every cell of the card's tables
  // that has a row in the form; checkboxes as "true"/"" like the server's draft.
  readCells() {
    return Object.entries(this.tablesValue).flatMap(([blockId, table]) =>
      this.tableRows(blockId).flatMap(({ rowLabel, prefix }) =>
        Object.entries(table.columns).map(([columnKey, column]) => ({
          block_id: Number(blockId),
          row_label: rowLabel,
          column_key: columnKey,
          value: readCellInput(this.cellInput(prefix, columnKey), column.type),
        })),
      ),
    )
  }

  // Writes one cell into the row of its label; false when the form has no such
  // row or column.
  writeCell(cell) {
    const table = this.tablesValue[String(cell.block_id)]
    const column = table?.columns[cell.column_key]
    const row = this.tableRows(cell.block_id).find((entry) => entry.rowLabel === cell.row_label)
    const input = row && column && this.cellInput(row.prefix, cell.column_key)
    if (!input) return false

    if (column.type === "checkbox") {
      input.checked = cell.value === "true" || cell.value === true
    } else {
      input.value = cell.value ?? ""
    }
    notify(input)
    this.cellTitles?.set(input.name, { title: this.cellTitle(cell), type: column.type })
    return true
  }

  cellTitle(cell) {
    const table = this.tablesValue[String(cell.block_id)]
    const column = table?.columns[cell.column_key]?.label || cell.column_key
    return [table?.label, cell.row_label, column].filter(Boolean).join(" · ")
  }

  // The form rows of a table that are not marked for removal: their row label and
  // the name prefix of their inputs.
  tableRows(blockId) {
    const form = this.form()
    return Array.from(form.querySelectorAll(`input[type="hidden"][name$="${BLOCK_ID_SUFFIX}"]`))
      .filter((input) => input.value === String(blockId))
      .map((input) => input.name.slice(0, -BLOCK_ID_SUFFIX.length))
      .filter(
        (prefix) =>
          !["1", "true"].includes(form.querySelector(`[name="${prefix}[_destroy]"]`)?.value),
      )
      .map((prefix) => ({
        prefix,
        rowLabel: form.querySelector(`[name="${prefix}[data][row_label]"]`)?.value ?? "",
      }))
  }

  // The visible input of a cell (a checkbox, not Rails' hidden "0" companion).
  cellInput(prefix, columnKey) {
    const name = `${prefix}[data][${columnKey}]`
    return this.form().querySelector(
      `input[name="${name}"]:not([type="hidden"]), select[name="${name}"], textarea[name="${name}"]`,
    )
  }

  // --- Writing and reading the card's fields ---

  // Writes one value into the card's input for +key+; false when the form has
  // no input that can take it.
  write(key, value) {
    const type = this.fieldsValue[key]?.type
    const name = `custom_report[field_data][${key}]`
    const scope = this.form()
    switch (type) {
      case "checkbox": {
        const box = scope.querySelector(`input[type="checkbox"][name="${name}"]`)
        if (!box) return false
        box.checked = value === "true" || value === true
        notify(box)
        return true
      }
      case "radio": {
        const radios = Array.from(scope.querySelectorAll(`input[type="radio"][name="${name}"]`))
        if (blank(value)) {
          radios.forEach((radio) => (radio.checked = false))
          if (radios[0]) notify(radios[0])
          return radios.length > 0
        }
        const radio = radios.find((input) => input.value === value)
        if (!radio) return false
        radio.checked = true
        notify(radio)
        return true
      }
      case "dropdown": {
        const select = scope.querySelector(`select[name="${name}"]`)
        return select ? this.setSelect(select, value ?? "") : false
      }
      case "multi_select":
        return this.writeMulti(scope, `${name}[]`, Array.isArray(value) ? value : [])
      case "date":
      case "datetime": {
        const hidden = scope.querySelector(`input[type="hidden"][name="${name}"]`)
        if (!hidden) return false
        hidden.value = value ?? ""
        notify(hidden)
        return true
      }
      default: {
        const input = scope.querySelector(
          `input[name="${name}"]:not([type="hidden"]), textarea[name="${name}"]`,
        )
        if (!input) return false
        input.value = value ?? ""
        notify(input)
        return true
      }
    }
  }

  writeMulti(scope, name, values) {
    const select = scope.querySelector(`select[name="${name}"]`)
    if (select) return this.setSelect(select, values)

    const boxes = Array.from(scope.querySelectorAll(`input[type="checkbox"][name="${name}"]`))
    if (!boxes.length || values.some((value) => !boxes.some((box) => box.value === value))) {
      return false
    }
    boxes.forEach((box) => (box.checked = values.includes(box.value)))
    notify(boxes[0])
    return true
  }

  // SlimSelect reverts a bare select.value; drive the select controller. A
  // picker's typed-in name (`text:<name>`) is not an option yet — add it first,
  // as the select's own "addable" would.
  setSelect(select, value) {
    const values = (Array.isArray(value) ? value : [value]).filter((v) => !blank(v))
    const prefix = this.freeTextPrefixValue
    const known = (v) => Array.from(select.options).some((option) => option.value === v)
    const missing = values.filter((v) => !known(v))
    if (missing.some((v) => !v.startsWith(prefix))) return false

    missing.forEach((v) => select.add(new Option(v.slice(prefix.length), v)))
    const controller = this.application.getControllerForElementAndIdentifier(select, "select")
    if (controller) {
      if (missing.length) controller.refresh()
      if (!values.length) {
        controller.clear()
      } else {
        controller.slimselect.setSelected(select.multiple ? values : values[0])
      }
    }
    Array.from(select.options).forEach((option) => {
      option.selected = values.includes(option.value)
    })
    notify(select)
    return true
  }

  readCard() {
    return Object.fromEntries(this.keys().map((key) => [key, this.read(key)]))
  }

  read(key) {
    const type = this.fieldsValue[key]?.type
    const name = `custom_report[field_data][${key}]`
    const scope = this.form()
    switch (type) {
      case "checkbox":
        return scope.querySelector(`input[type="checkbox"][name="${name}"]`)?.checked
          ? "true"
          : "false"
      case "radio":
        return scope.querySelector(`input[type="radio"][name="${name}"]:checked`)?.value ?? ""
      case "dropdown":
        return scope.querySelector(`select[name="${name}"]`)?.value ?? ""
      case "multi_select": {
        const select = scope.querySelector(`select[name="${name}[]"]`)
        const chosen = select
          ? Array.from(select.selectedOptions)
          : Array.from(scope.querySelectorAll(`input[type="checkbox"][name="${name}[]"]:checked`))
        return chosen.map((element) => element.value).filter((v) => v !== "")
      }
      case "date":
      case "datetime":
        return scope.querySelector(`input[type="hidden"][name="${name}"]`)?.value ?? ""
      default:
        return (
          scope.querySelector(
            `input[name="${name}"]:not([type="hidden"]), textarea[name="${name}"]`,
          )?.value ?? ""
        )
    }
  }

  // What a tile shows: the option text a select or radio displays, Ja, German
  // dates and decimals.
  display(key) {
    const type = this.fieldsValue[key]?.type
    const value = this.read(key)
    const name = `custom_report[field_data][${key}]`
    const scope = this.form()
    if (type === "checkbox") return value === "true" ? this.message("yes") : ""
    if (type === "dropdown" || type === "multi_select") {
      const select = scope.querySelector(`select[name="${name}"], select[name="${name}[]"]`)
      if (select) {
        return Array.from(select.selectedOptions)
          .filter((option) => option.value !== "")
          .map((option) => option.textContent.trim())
          .join(", ")
      }
      return Array.isArray(value) ? value.join(", ") : value
    }
    if (type === "radio") {
      const radio = scope.querySelector(`input[type="radio"][name="${name}"]:checked`)
      return (radio && scope.querySelector(`label[for="${radio.id}"]`)?.textContent.trim()) || value
    }
    if (type === "date" || type === "datetime") return germanDate(value)
    if (type === "number" || type === "currency") return String(value).replace(".", ",")
    return value
  }

  hiddenByCondition(key) {
    const wrapper = this.form().querySelector(
      `[data-dynamic-form-target="field"][data-field-key="${CSS.escape(key)}"]`,
    )
    return !!wrapper?.closest(".hidden")
  }

  // --- Helpers ---

  reportParams() {
    const params = {}
    if (this.reportIdValue) params.custom_report_id = this.reportIdValue
    if (this.reportableTypeValue) {
      params.reportable_type = this.reportableTypeValue
      params.reportable_id = this.reportableIdValue
    }
    return params
  }

  setBlocked(blocked) {
    this.blocked = blocked
    if (this.hasMicTarget) {
      this.micTarget.disabled = blocked || ACTIVE_STATES.includes(this.element.dataset.state)
      this.micTarget.title = blocked ? this.message("busy") : ""
    }
  }

  announce(name) {
    window.dispatchEvent(new CustomEvent(name, { detail: { element: this.element } }))
  }

  keys() {
    return Object.keys(this.fieldsValue)
  }
}

function blank(value) {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "false" ||
    (Array.isArray(value) && value.length === 0)
  )
}

function cellKey(cell) {
  return `${cell.block_id}/${cell.row_label}/${cell.column_key}`
}

function readCellInput(input, type) {
  if (!input) return ""
  if (type === "checkbox") return input.checked ? "true" : ""
  return input.value ?? ""
}

function notify(input) {
  input.dispatchEvent(new Event("input", { bubbles: true }))
  input.dispatchEvent(new Event("change", { bubbles: true }))
}

// "2026-11-02" / "2026-11-02 07:30" -> "02.11.2026" / "02.11.2026 07:30"
function germanDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})(.*)$/)
  return match ? `${match[3]}.${match[2]}.${match[1]}${match[4]}` : value
}
